import React from "react";

import { ChronoUnit, ZonedDateTime, convert } from "@js-joda/core";
import CalendarWrapper from "components/basic/calendar-wrapper/CalendarWrapper";
import CalendarHeatmap from "components/dashboard/calendar-heatmap/CalendarHeatmap";
import { AnalyticsResultDto, MdmObjectDto, useGetAnalyticsByObjectIdQuery, useGetAnalyticsByProjectIdAndDateRangeQuery } from "gql/generated/types";
import { Link, useParams } from "react-router-dom";
import { TableColumn } from "utils/types";
import ItemsTable from "./ItemsTable";

type ObjectAnalyticsProps = {
  object?: MdmObjectDto;
  projectId?: string;
};

const ObjectAnalytics: React.FC<ObjectAnalyticsProps> = ({ object, projectId }) => {
  const halfYearAgo = convert(ZonedDateTime.now().truncatedTo(ChronoUnit.DAYS).minus(6, ChronoUnit.MONTHS)).toDate();
  const [dateFrom, setDateFrom] = React.useState<Date>(halfYearAgo);
  const [dateTo, setDateTo] = React.useState<Date>(new Date());
  const {projectId: proId} = useParams();

  const commonColumns: TableColumn<AnalyticsResultDto>[] = [
    { field: "id.date", header: "Date", sortable: true, filter: true, body: rowData => new Date(rowData.id?.date).toLocaleString() },
    { field: "id.analyticsType", header: "Type", sortable: true, filter: true },
    { field: "description", header: "Description"},
  ];

  const renderObjectLink = (rowData: AnalyticsResultDto) => (
    <Link to={`/${proId}/objects/${rowData.mdmObject?.id}`}>{rowData.mdmObject?.title}</Link>
  );

  const columns: TableColumn<AnalyticsResultDto>[] = !object ? [
    ...commonColumns,
    { field: "mdmObject.id", header: "Object", body: renderObjectLink },
  ] : [...commonColumns];

  const {data: analytics, loading} = useGetAnalyticsByObjectIdQuery({
    variables: {
      objectId: object?.id!,
      range: {
        start: dateFrom?.toISOString(),
        end: dateTo?.toISOString(),
        endInclusive: true,
        startInclusive: true
      }
    },
    skip: !object?.id
  })

  const {data, loading: loading2} = useGetAnalyticsByProjectIdAndDateRangeQuery({
    variables: {
      projectId: projectId!,
      range: {
        start: dateFrom?.toISOString(),
        end: dateTo?.toISOString(),
        endInclusive: true,
        startInclusive: true
      }
    },
    skip: !projectId
  })

  const analyticsData = (analytics?.allAnalyticsResultsByObjectIdAndDateRange ??
    data?.allAnalyticsResultsByProjectIdAndDateRange ??
    []) as AnalyticsResultDto[];
  const isLoading = loading || loading2;
  
  return (
    <div>
      <div className="flex gap-2 mb-5 z">
        <div className="flex flex-column gap-2">
          <label>Date from:</label>
          <CalendarWrapper value={dateFrom} showTime={false} onChange={date => setDateFrom(date)} />
        </div>
        <div className="flex flex-column gap-2">
          <label>Date to:</label>
          <CalendarWrapper value={dateTo} showTime={false} onChange={date => setDateTo(date)} />
        </div>
      </div>
      <CalendarHeatmap
        startDate={dateFrom}
        endDate={dateTo}
        highValueColor="ed0000"
        lowValueColor="ffffff"
        maxValue={10}
        minValue={0}
        lowValueTitle="cccc"
        highValueTitle="wwww"
        showDateSelection={false}
        staticMinMaxColors
        staticMinMaxTitles={false}
        staticMinMaxValues
        isCustomDataset
      />
      <ItemsTable
        columns={columns}
        data={analyticsData}
        isLoading={isLoading}
        showToolbar={false}
      />
    </div>
  );
}

export default ObjectAnalytics;